<template>
  <section class="reports page-content">
    <div class="page-title">
      <span>{{ $lang.messages.reportsHeadline.toUpperCase() }}</span>
    </div>
    <div class="page-wrapper-reports">
      <!-- Expense & Payments Report -->
      <div class="box expense-wrapper">
        <h5>{{ $lang.messages.reportsExpensePayment }}</h5>
        <hr>
        <p>{{ $lang.messages.reportsExpensePaymentText }}</p>

        <!-- Input fields -->
        <section class="expense-reports-box">

          <!-- Date From -->
          <div class="input-container datepicker-container">
            <p class="input-filter-label">{{ $lang.messages.from }}</p>
            <f-p-datepicker
              ref="dateFrom"
              @optionChanged="onSelectedDateFrom"
              @click="onClickDateFrom">
            </f-p-datepicker>
            <div class="error">
              <img
                v-show="(errors.has('dateFrom') && !blurredDatepickerProp)"
                src="@/assets/images/validation_ico.png"
                alt="Red exclamation mark"
                class="error-icon" />
              <div class="error-tooltip" v-show="errors.has('dateFrom') && !blurredDatepickerProp">
                <div class="text">
                  {{ errors.first('dateFrom') }}
                </div>
              </div>
            </div>
          </div>

          <!-- Date To -->
          <div class="input-container datepicker-container">
            <p class="input-filter-label">{{ $lang.messages.to }}</p>
            <f-p-datepicker
                @optionChanged="onSelectedDateTo">
            </f-p-datepicker>
          </div>

          <!-- Spouse -->
          <div class="input-container">
            <p class="input-filter-label">{{ $lang.messages.reportsInputSpouse }}</p>
              <autocomplete
                ref="recipient"
                :name="'recipient'"
                :items="coparents"
                :placeholder="$lang.messages.reportsInputSpouse"
                :validateProp="false"
                @optionChanged="onSelectedRecipient"
              />
          </div>
        </section>

        <!-- Download section -->
        <section class="expense-attachment-box" :class="{ 'no-download-attachment': payDownloadReport || errors.has('dateFrom') }">
          <!-- PDF -->
          <div class="attachment-wrapper">
            <img src="@/assets/images/payments/pdf_ico.png" alt="pdf">
            <span>{{ $lang.messages.payPdfReport }}</span>
            <span class="disabled-button" :class="{ 'progress-cursor': setCursorDownload }">
              <img src="@/assets/images/payments/download_ico.png" alt="Download" />
              <button @click="getPayPdfReport">
                {{ $lang.messages.download }}
              </button>
            </span>
          </div>
          <!-- Excel -->
          <div class="attachment-wrapper">
            <img src="@/assets/images/payments/xls_ico.png" alt="Excel">
            <span>{{ $lang.messages.payExcelReport }}</span>
            <span class="disabled-button" :class="{ 'progress-cursor': setCursorDownload }">
              <img src="@/assets/images/payments/download_ico.png" alt="Download" />
              <button @click="getPayExcelReport">
                {{ $lang.messages.download }}
              </button>
            </span>
          </div>
        </section>
      </div>

      <!-- Message Report -->
      <div class="box msg-wrapper">
        <h5>{{ $lang.messages.reportsMessage }}</h5>
        <hr>
        <p>{{ $lang.messages.reportsMessageText }}</p>

        <div class="input-container">
          <p class="input-filter-label">{{ $lang.messages.reportsInputSpouse }}</p>

          <!-- Input fields -->
          <section>
            <autocomplete
              :name="'recipient'"
              :items="coparents"
              :placeholder="$lang.messages.reportsInputSpouse"
              :validateProp="false"
              @optionChanged="onSelectedMsgRecipient"
            />
          </section>

          <!-- Download section -->
          <section class="expense-attachment-box" :class="{ 'no-download-attachment': msgDownloadReport }">
            <!-- PDF -->
            <div class="attachment-wrapper">
              <img src="@/assets/images/payments/pdf_ico.png" alt="pdf">
              <span>{{ $lang.messages.payPdfReport }}</span>
              <span class="disabled-button" :class="{ 'progress-cursor': setCursorDownload }">
                <img src="@/assets/images/payments/download_ico.png" alt="Download" />
                <button @click="getMsgPdfReport">
                  {{ $lang.messages.download }}
                </button>
              </span>
            </div>
            <!-- Excel -->
            <div class="attachment-wrapper">
              <img src="@/assets/images/payments/xls_ico.png" alt="Excel">
              <span>{{ $lang.messages.payExcelReport }}</span>
              <span class="disabled-button" :class="{ 'progress-cursor': setCursorDownload }">
                <img src="@/assets/images/payments/download_ico.png" alt="Download" />
                <button @click="getMsgExcelReport">
                  {{ $lang.messages.download }}
                </button>
              </span>
            </div>
          </section>

        </div>

      </div>
    </div>
  </section>
</template>

<script>
  import Autocomplete from "@/components/common/Autocomplete";
  import {
    GET_PAY_REPORTS_EXCEL,
    GET_PAY_REPORTS_PDF,
    GET_MSG_REPORTS_PDF,
    GET_MSG_REPORTS_EXCEL
  } from "@/store/action.types";
  import { mapActions, mapGetters } from 'vuex';
  import { dateFormat, requestDateFormat, requestDateFormatEndOfDay } from '@/mixins/dateFormat';
  import FPDatepicker from '@/components/common/FPDatepicker';
  import STATUS_CODES from "@/common/statusCodes";

  export default {
    name: "reports",
    components: {
      Autocomplete,
      FPDatepicker
    },
    mixins: [
      dateFormat,
      requestDateFormat,
      requestDateFormatEndOfDay
    ],
    data() {
      return {
        typeable: true,
        dateFrom: new Date(),
        dateTo: new Date(),
        payDownloadReport: true,
        msgDownloadReport: true,
        coparentGuidReport: '',
        downloadPdfLink: '',
        setCursorDownload: false,
        timeOffset: '',
        blurredDatepickerProp: false
      };
    },
    computed: {
      ...mapGetters({
        coparentsList: 'coparents',
        countryCodeString: 'countryCode',
        coparentGuid: 'coparentGuid',
      }),
      coparents() {
        const x = this;
        return this.coparentsList.filter(item => {
          return item.guid !== x.coparentGuid
        })
      }
    },
    methods: {
      ...mapActions({
        getPreviewPaymentsReportsPdf: GET_PAY_REPORTS_PDF,
        getPreviewPaymentsReportsExcel: GET_PAY_REPORTS_EXCEL,
        getPreviewMsgReportsPdf: GET_MSG_REPORTS_PDF,
        getPreviewMsgReportsExcel: GET_MSG_REPORTS_EXCEL
      }),
      getTimeOffset() {
        /*
         * @description - Calculate the offest in hours for timezones
         * @returns {String} - '+01:00' or '-04:30'
        */
        const date = this.requestDateFormat(new Date());
        return date.substr(date.length - 6)
      },
      onSelectedRecipient(value) {
        this.recipient = value;
        this.coparentGuidReport = this.recipient.guid;
        this.payDownloadReport = false;
      },
      onSelectedMsgRecipient(value) {
        this.recipient = value;
        this.coparentGuidReport = this.recipient.guid;
        this.msgDownloadReport = false;
      },
      onSelectedDateFrom(value) {
        this.dateFrom = value;
        this.validateDates();
      },
      onSelectedDateTo(value) {
        this.dateTo = value;
        this.validateDates();
      },
      onClickDateFrom(value) {
        this.blurredDatepickerProp = value;
      },
      validateDates() {
        let dateFrom = new Date(this.dateFrom)
        dateFrom.setMilliseconds(0)
        let dateTo = new Date(this.dateTo)
        dateTo.setMilliseconds(0)

        if (!(dateFrom.getTime() <= dateTo.getTime())) {
          this.$validator.errors.add({
            field: 'dateFrom',
            msg: this.$lang.messages.vee_dateFromBeforeDateTo
          });
          this.$refs.dateFrom.setError(true);
        } else {
          this.$validator.errors.remove('dateFrom');
          this.$refs.dateFrom.setError(false);
        }
      },
      postPaymentReport() {
        return {
          originalPaymentDateFrom: this.requestDateFormat(this.dateFrom),
          originalPaymentDateTo: this.requestDateFormatEndOfDay(this.dateTo),
          reimbursementPaymentDateFrom: this.requestDateFormat(this.dateFrom),
          reimbursementPaymentDateTo: this.requestDateFormatEndOfDay(this.dateTo),
          coparentGuid: this.coparentGuidReport
        }
      },
      postMsgReport() {
        return {
          coparentGuid: this.coparentGuidReport,
          languageCode: 'en',
          countryCode: this.countryCodeString,
          zoneId: this.getTimeOffset()
        }
      },
      downloadReport(res, fileReportName, pdfOrExcel) {
        const linkSource = `data:application/${pdfOrExcel};base64,${res}`;
        const downloadLink = document.createElement('a');
        const fileName = fileReportName;

        downloadLink.href = linkSource;
        downloadLink.download = fileName;
        setTimeout(() => {
            downloadLink.dispatchEvent(new MouseEvent(`click`, {bubbles: true, cancelable: true, view: window}));
        }, 0);
      },
      getPayPdfReport() {
        this.setCursorDownload = true
        this.getPreviewPaymentsReportsPdf(
          this.postPaymentReport()
        )
        .then( response => {
          // When there are no reports to be downloaded
          // show the snotify info message
          if (response.status === 204) {
            this.$snotify.info(this.$lang.messages.sn_reportDontExist, 'INFO')
            this.setCursorDownload = false
            return
          }
          // If first date ( dateFrom ) is bigger than second date ( dateTo )
          // don't call the API for the reports
          if (new Date(this.dateFrom).getTime() > new Date(this.dateTo).getTime()) {
            return
          }
          this.downloadReport(response.data.data.payload, response.data.data.filename, response.data.data.extension)
          this.setCursorDownload = false
        })
        .catch(error => {
          if (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402) {
            this.$snotify.error(error.response.data.status.desc, 'ERROR')
          }
        })
      },
      getPayExcelReport() {
        this.setCursorDownload = true
        this.getPreviewPaymentsReportsExcel(
          this.postPaymentReport()
        )
        .then( response => {
          // When there are no reports to be downloaded
          // show the snotify info message
          if (response.status === 204) {
            this.$snotify.info(this.$lang.messages.sn_reportDontExist, 'INFO')
            this.setCursorDownload = false
            return
          }
          // If first date ( dateFrom ) is bigger than second date ( dateTo )
          // don't call the API for the reports
          if (new Date(this.dateFrom).getTime() > new Date(this.dateTo).getTime()) {
            return
          }
          this.downloadReport(response.data.data.payload, response.data.data.filename, response.data.data.extension)
          this.setCursorDownload = false
        })
        .catch(error => {
          if (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402) {
            this.$snotify.error(error.response.data.status.desc, 'ERROR')
          }
        })
      },
      getMsgPdfReport() {
        this.setCursorDownload = true
        this.getPreviewMsgReportsPdf(
          this.postMsgReport()
        )
          .then( response => {
            // When there are no reports to be downloaded
            // show the snotify info message
            if (response.status === 204) {
              this.$snotify.info(this.$lang.messages.sn_conversationDontExist, 'INFO')
              this.setCursorDownload = false
              return
            }
            // If first date ( dateFrom ) is bigger than second date ( dateTo )
            // don't call the API for the reports
            if (new Date(this.dateFrom).getTime() > new Date(this.dateTo).getTime()) {
              return
            }
            this.downloadReport(response.data.data.payload, response.data.data.filename, response.data.data.extension)
            this.setCursorDownload = false
          })
          .catch(error => {
            if (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402) {
              this.$snotify.error(error.response.data.status.desc, 'ERROR')
            }
          })
      },
      getMsgExcelReport() {
        this.setCursorDownload = true
        this.getPreviewMsgReportsExcel(
            this.postMsgReport()
        )
          .then( response => {
            // When there are no reports to be downloaded
            // show the snotify info message
            if (response.status === 204) {
              this.$snotify.info(this.$lang.messages.sn_conversationDontExist, 'INFO')
              this.setCursorDownload = false
              return
            }
            // If first date ( dateFrom ) is bigger than second date ( dateTo )
            // don't call the API for the reports
            if (new Date(this.dateFrom).getTime() > new Date(this.dateTo).getTime()) {
              return
            }
            this.downloadReport(response.data.data.payload, response.data.data.filename, response.data.data.extension)
            this.setCursorDownload = false
          })
          .catch(error => {
            if (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402) {
              this.$snotify.error(error.response.data.status.desc, 'ERROR')
            }
          })
      }
    }
  };
</script>
